import React, { useEffect } from 'react';
import { appWithTranslation } from 'next-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SiteInfoProvider } from '@contexts/SiteInfoContext';
import { HeaderProvider } from '@contexts/HeaderContext';
import { LiveScoreProvider } from '@contexts/LiveScoreContext';
import { LoginDataProvider } from '@contexts/LoginDataContext';
import { UMDataProvider } from '@contexts/UMDataContext';
import { datadogRum } from '@datadog/browser-rum';
import Script from 'next/script';
import Head from 'next/head';
import { RegisterProvider } from '@contexts/RegisterContext';
import ServerPath from '../configs/serverPath';
import { getGtmData } from '../configs/gtmConfig';
import fileVersion from '../fileVersion';

const ProviderComposer = ({ providers = [], children }) => (
    <>
        {providers.reduceRight((child, parent) => (
            React.cloneElement(parent, {}, child)
        ), children)}
    </>
);

const App = ({ Component, pageProps }) => {
    const queryClient = new QueryClient();

    const {
        siteId,
        siteName,
        enumSiteName,
        isDeniedIp,
        languageList = [],
        loginWebImageServerHost,
        mobileImageServerHost,
        underMaintenanceInfo,
        desktopBanners,
        desktopRightDownBanners,
        mobileBanners,
        vendorSiteId,
        useImageServer,
        openGesture,
        autoColorScheme,
        preferredSiteName,
        clientApiUrl,
    } = pageProps;

    const siteNameLowerCase = siteName?.toLowerCase();
    const versionQueryId = fileVersion ? `v=${fileVersion}` : 'v=1.0.0';
    const isAutoColorSchemeShow = autoColorScheme?.colorScheme ? autoColorScheme.colorScheme !== 'customize' : false;
    let cssFileName = '';
    const composePath = (pathSetting) => {
        const { pathType, language, pathName } = pathSetting;
        const commonPathResb = useImageServer
            ? `${loginWebImageServerHost}/resb`
            : '/resb';
        const commonPathResh = useImageServer
            ? `${loginWebImageServerHost}/resh`
            : '/resh';
        const commonScriptPath = '/b';
        if (siteNameLowerCase === 'pica88') {
            cssFileName = 'neweu';
        } else {
            cssFileName = siteNameLowerCase;
        }

        let queryString = versionQueryId;
        const customEncodeURIComponent = (str) => {
            return encodeURIComponent(str).replace(/[.]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
        };

        if (pathType === ServerPath.helpUiHtmlGlobal && isAutoColorSchemeShow) {
            queryString = customEncodeURIComponent(`colorscheme=${autoColorScheme.colorScheme}`
            + `&darkMode=${autoColorScheme.darkMode}`
            + `&AutoPrimary_h=${autoColorScheme.primary.autoHsl.hue}`
            + `&AutoPrimary_s=${autoColorScheme.primary.autoHsl.saturation}`
            + `&AutoPrimary_l=${autoColorScheme.primary.autoHsl.lightness}`
            + `&AutoSecondary_h=${autoColorScheme.secondary.autoHsl.hue}`
            + `&AutoSecondary_s=${autoColorScheme.secondary.autoHsl.saturation}`
            + `&AutoSecondary_l=${autoColorScheme.secondary.autoHsl.lightness}`
            + `&PreferredSiteName=${preferredSiteName}`
            + `&AutoLogo=url(${commonPathResh}/${cssFileName}/common/images/logo_slogan/logo_en.svg?${versionQueryId}")`);
        }

        switch (pathType) {
        case ServerPath.globalImage:
            return `${commonPathResb}/_global/common/images/${pathName}?${queryString}`;

        case ServerPath.globalCss:
            return `${commonPathResb}/_global/common/css/${pathName}?${queryString}`;

        case ServerPath.globalScript:
            return `${commonScriptPath}/scripts/${pathName}?${queryString}`;

        case ServerPath.siteImage:
            return `${commonPathResb}/${cssFileName}/common/images/${pathName}?${queryString}`;

        case ServerPath.siteCss:
            return `${commonPathResb}/${cssFileName}/common/css/${pathName}?${queryString}`;

        case ServerPath.siteHtml:
            return `${commonPathResb}/${cssFileName}/${language}/${pathName}?${queryString}`;

        case ServerPath.helpUiHtml:
            return `${commonPathResh}/${cssFileName}/${language}/${pathName}?${queryString}`;

        case ServerPath.helpUiHtmlGlobal:
            return `${commonPathResh}/_global/${language}/${pathName}?${queryString}`;

        case ServerPath.helpUiGlobal:
            return `${commonPathResh}/_global/common/css/${pathName}?${queryString}`;

        case ServerPath.helpUiTheme:
            return `${commonPathResh}/${cssFileName}/common/css/${pathName}?${queryString}`;

        case ServerPath.landingPage:
            return `${commonPathResb}/${siteNameLowerCase}/external-landing-page${pathName}?${queryString}`;

        case ServerPath.colorscheme:
            return `${commonPathResb}/_global/common/css/${pathName}?${queryString}`;

        case ServerPath.autoColorscheme:
            return `${commonPathResb}/_global/common/images/before_nova88/${pathName}?${queryString}`;

        default:
            return '';
        }
    };

    const siteInfo = {
        ...pageProps,
        siteId,
        siteName: siteNameLowerCase,
        enumSiteName,
        isDeniedIp,
        languageList,
        loginWebImageServerHost,
        mobileImageServerHost,
        underMaintenanceInfo,
        desktopBanners,
        desktopRightDownBanners,
        mobileBanners,
        vendorSiteId,
        useImageServer,
        openGesture,
        composePath,
        isAutoColorSchemeShow,
        clientApiUrl,
    };

    useEffect(() => {
        if (autoColorScheme?.colorScheme) {
            document.querySelector('html').setAttribute('data-colorscheme', autoColorScheme.colorScheme);
        }
        if (siteInfo?.enableDataDogLoginWeb) {
            datadogRum.init({
                site: 'datadoghq.com',
                applicationId: 'b3cf95b1-09c1-4881-8f51-b9ac39bbb0b8',
                clientToken: 'pub35c51ec3e95c745b8bda3ae5856700ba',
                service: 'credit-prod',
                env: 'prod',
                // Specify a version number to identify the deployed version of your application in Datadog
                // version: '1.0.0',
                sessionSampleRate: 1,
                sessionReplaySampleRate: 0,
                trackBfcacheViews: true,
                defaultPrivacyLevel: 'mask-user-input',
                enableExperimentalFeatures: ['feature_flags'],
            });
            if (datadogRum) {
                datadogRum.addFeatureFlagEvaluation('platform', 'before');
            }
        }
    }, []);


    // GTM suggest from https://nextjs.org/docs/messages/next-script-for-ga
    return (
        <>
            <Head>
                <style>
                    {autoColorScheme?.primary && autoColorScheme?.secondary && `
                [data-colorscheme*="auto"] {
                --auto-primary-h: ${autoColorScheme.primary.autoHsl.hue};
                --auto-primary-s: ${autoColorScheme.primary.autoHsl.saturation}%;
                --auto-primary-l: ${autoColorScheme.primary.autoHsl.lightness}%;

                --auto-secondary-h: ${autoColorScheme.secondary.autoHsl.hue};
                --auto-secondary-s: ${autoColorScheme.secondary.autoHsl.saturation}%;
                --auto-secondary-l: ${autoColorScheme.secondary.autoHsl.lightness}%;

                --auto-bet-odds-text: ${autoColorScheme.oddsText};
                --auto-bet-odds-minus-text: ${autoColorScheme.oddsMinus};
                --auto-league-header-bg: ${autoColorScheme.leagueHeader};
                --auto-league-text: ${autoColorScheme.leagueText};

                /* Logo */
                --auto-logo: url(${composePath({ pathType: ServerPath.siteImage, pathName: 'logo_slogan/logo_en.svg' })});     
                --auto-logo-w: url(${composePath({ pathType: ServerPath.siteImage, pathName: 'logo_slogan/logo_en_w.svg' })});
                }`}
                </style>
            </Head>
            <Script // <Script /> needs to be imported in _app.jsx
                src={composePath({
                    pathType: ServerPath.globalScript,
                    pathName: 'jquery-2.1.4.min.js',
                })}
                strategy="beforeInteractive"
            />
            <Script
                src={composePath({
                    pathType: ServerPath.globalScript,
                    pathName: 'GestureClass.js',
                })}
                strategy="beforeInteractive"
            />
            {/* <!-- 添加Google Tag Manager代碼 --> */}
            <Script id="google-tag-manager" strategy="beforeInteractive">
                { getGtmData(siteName) }
            </Script>
            {/* <!-- 結束Google Tag Manager代碼 --> */}
            <QueryClientProvider client={queryClient}>
                <ProviderComposer providers={[
                    <SiteInfoProvider siteInfo={siteInfo} />,
                    <LiveScoreProvider />,
                    <HeaderProvider />,
                    <LoginDataProvider />,
                    <UMDataProvider />,
                    <RegisterProvider />,
                ]}
                >
                    <Component {...pageProps} />
                </ProviderComposer>
            </QueryClientProvider>
        </>

    );
};

export default appWithTranslation(App);
